import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Ban, Plus, Trash2, Edit, Calendar } from '../icons/wp-icons';
import Layout from '../components/Layout/Layout';
import Modal from '../components/Common/Modal';
import {
  getAllBlockierteZeiten,
  createBlockierteZeit,
  updateBlockierteZeit,
  deleteBlockierteZeit,
  getFeiertageConfig,
  updateFeiertageConfig
} from '../api/kalenderApi';

function BlockierteZeiten() {
  const navigate = useNavigate();
  const [blockierteZeiten, setBlockierteZeiten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    titel: '',
    startDatum: '',
    endDatum: '',
    ganztags: true,
    startzeit: '09:00',
    endzeit: '17:00',
    grund: ''
  });

  // Feiertage-Konfiguration (Toggle + Bundesland + Vorschau)
  const [feiertageConfig, setFeiertageConfig] = useState(null);
  const [savingFeiertage, setSavingFeiertage] = useState(false);

  useEffect(() => {
    loadBlockierteZeiten();
    loadFeiertageConfig();
  }, []);

  const loadFeiertageConfig = async () => {
    try {
      const data = await getFeiertageConfig();
      setFeiertageConfig(data);
    } catch (error) {
      console.error('Error loading Feiertage config:', error);
    }
  };

  const handleFeiertageChange = async (changes) => {
    if (!feiertageConfig) return;
    const next = { ...feiertageConfig, ...changes };
    setFeiertageConfig(next);
    setSavingFeiertage(true);
    try {
      const data = await updateFeiertageConfig({
        feiertageBlockiert: next.feiertageBlockiert,
        bundesland: next.bundesland
      });
      setFeiertageConfig(prev => ({ ...prev, ...data }));
    } catch (error) {
      console.error('Error saving Feiertage config:', error);
      // Revert
      loadFeiertageConfig();
    } finally {
      setSavingFeiertage(false);
    }
  };

  const loadBlockierteZeiten = async () => {
    try {
      setLoading(true);
      const data = await getAllBlockierteZeiten();
      setBlockierteZeiten(data);
    } catch (error) {
      console.error('Error loading blockierte zeiten:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      titel: '',
      startDatum: '',
      endDatum: '',
      ganztags: true,
      startzeit: '09:00',
      endzeit: '17:00',
      grund: ''
    });
    setEditingItem(null);
  };

  const openCreateModal = () => {
    resetForm();
    // Standard: Heutiges Datum
    const today = new Date().toISOString().split('T')[0];
    setFormData(prev => ({
      ...prev,
      startDatum: today,
      endDatum: today
    }));
    setIsModalOpen(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      titel: item.titel,
      startDatum: new Date(item.startDatum).toISOString().split('T')[0],
      endDatum: new Date(item.endDatum).toISOString().split('T')[0],
      ganztags: item.ganztags,
      startzeit: item.startzeit || '09:00',
      endzeit: item.endzeit || '17:00',
      grund: item.grund || ''
    });
    setIsModalOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.titel || !formData.startDatum || !formData.endDatum) {
      alert('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    try {
      setSaving(true);

      const data = {
        titel: formData.titel,
        startDatum: formData.startDatum,
        endDatum: formData.endDatum,
        ganztags: formData.ganztags,
        startzeit: formData.ganztags ? null : formData.startzeit,
        endzeit: formData.ganztags ? null : formData.endzeit,
        grund: formData.grund || null
      };

      if (editingItem) {
        await updateBlockierteZeit(editingItem.id, data);
      } else {
        await createBlockierteZeit(data);
      }

      setIsModalOpen(false);
      resetForm();
      await loadBlockierteZeiten();
    } catch (error) {
      console.error('Error saving blockierte zeit:', error);
      alert('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Möchten Sie diese blockierte Zeit wirklich löschen?')) {
      return;
    }

    try {
      await deleteBlockierteZeit(id);
      await loadBlockierteZeiten();
    } catch (error) {
      console.error('Error deleting blockierte zeit:', error);
      alert('Fehler beim Löschen');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const timeOptions = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const time = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      timeOptions.push(time);
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-8 flex justify-center items-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 overflow-x-hidden">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <button
              onClick={() => navigate('/kalender')}
              className="p-2 hover:bg-gray-100 rounded-lg transition shrink-0"
            >
              <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <Ban className="w-6 h-6 sm:w-8 sm:h-8 text-danger shrink-0" />
              <h1 className="text-xl sm:text-3xl font-serif font-bold truncate">Blockierte Zeiten</h1>
            </div>
          </div>

          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 bg-primary text-white px-3 sm:px-4 py-2 rounded-button font-medium hover:bg-primary-dark transition shrink-0"
          >
            <Plus className="w-5 h-5" />
            <span className="hidden sm:inline">Neue Blockierung</span>
            <span className="sm:hidden">Neu</span>
          </button>
        </div>

        {/* Info */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">
            Blockierte Zeiten sind Zeiträume, in denen keine Termine gebucht werden können.
            Nutzen Sie dies für Urlaub, Feiertage oder andere geschlossene Tage.
          </p>
        </div>

        {/* Gesetzliche Feiertage automatisch blockieren */}
        {feiertageConfig && (
          <div className="bg-surface rounded-card shadow-card p-5 mb-6">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <h2 className="font-semibold text-base">Gesetzliche Feiertage automatisch blockieren</h2>
                <p className="text-sm text-text-muted mt-1">
                  Sperrt alle Feiertage des gewählten Bundeslandes ganztägig — der KI-Agent
                  bietet keine Termine an diesen Tagen an, im Kalender sind sie rot markiert.
                </p>
              </div>
              <div
                onClick={() => handleFeiertageChange({ feiertageBlockiert: !feiertageConfig.feiertageBlockiert })}
                className={`relative w-11 h-6 rounded-full transition cursor-pointer flex-shrink-0 ${feiertageConfig.feiertageBlockiert ? 'bg-primary' : 'bg-gray-300'}`}
              >
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${feiertageConfig.feiertageBlockiert ? 'translate-x-5' : ''}`} />
              </div>
            </div>

            {feiertageConfig.feiertageBlockiert && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Bundesland</label>
                  <select
                    value={feiertageConfig.bundesland || ''}
                    onChange={(e) => handleFeiertageChange({ bundesland: e.target.value || null })}
                    disabled={savingFeiertage}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-white"
                  >
                    <option value="">Bundesland wählen…</option>
                    {feiertageConfig.bundeslaender?.map(bl => (
                      <option key={bl.code} value={bl.code}>{bl.name}</option>
                    ))}
                  </select>
                </div>

                {feiertageConfig.bundesland && feiertageConfig.vorschau?.length > 0 && (
                  <div className="rounded-lg bg-gray-50 border border-border p-3">
                    <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">
                      Nächste Feiertage
                    </p>
                    <ul className="space-y-1">
                      {feiertageConfig.vorschau.map(h => (
                        <li key={h.date} className="flex items-center gap-2 text-sm">
                          <Calendar className="w-3.5 h-3.5 text-text-muted shrink-0" />
                          <span className="tabular-nums text-text-muted w-24 shrink-0">
                            {new Date(h.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </span>
                          <span>{h.name}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {feiertageConfig.bundesland && feiertageConfig.vorschau?.length === 0 && (
                  <p className="text-xs text-text-muted">In den nächsten 12 Monaten gibt es keine Feiertage in diesem Bundesland.</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Liste */}
        {blockierteZeiten.length === 0 ? (
          <div className="bg-surface rounded-card shadow-card p-8 text-center">
            <Ban className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-text-muted">Keine blockierten Zeiten vorhanden.</p>
            <button
              onClick={openCreateModal}
              className="mt-4 text-primary hover:underline"
            >
              Erste Blockierung hinzufügen
            </button>
          </div>
        ) : (
          <>
            {/* Mobile: Cards */}
            <div className="md:hidden space-y-3">
              {blockierteZeiten.map((bz) => (
                <div key={bz.id} className="bg-surface rounded-card shadow-card p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-medium text-lg">{bz.titel}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full inline-block mt-1 ${
                        bz.ganztags
                          ? 'bg-red-100 text-red-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {bz.ganztags ? 'Ganztags' : 'Teilweise'}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => openEditModal(bz)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition"
                      >
                        <Edit className="w-5 h-5 text-primary" />
                      </button>
                      <button
                        onClick={() => handleDelete(bz.id)}
                        className="p-2 hover:bg-red-50 rounded-lg transition"
                      >
                        <Trash2 className="w-5 h-5 text-danger" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-text-muted">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(bz.startDatum)}</span>
                    {bz.startDatum !== bz.endDatum && (
                      <>
                        <span>-</span>
                        <span>{formatDate(bz.endDatum)}</span>
                      </>
                    )}
                  </div>
                  {!bz.ganztags && (
                    <div className="text-sm text-text-muted mt-1">
                      {bz.startzeit} - {bz.endzeit}
                    </div>
                  )}
                  {bz.grund && (
                    <p className="text-sm text-text-muted mt-2 border-t border-border pt-2">
                      {bz.grund}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop: Table */}
            <div className="hidden md:block bg-surface rounded-card shadow-card overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-border">
                  <tr>
                    <th className="text-left py-4 px-6 font-medium">Titel</th>
                    <th className="text-left py-4 px-6 font-medium">Zeitraum</th>
                    <th className="text-left py-4 px-6 font-medium">Typ</th>
                    <th className="text-left py-4 px-6 font-medium">Grund</th>
                    <th className="text-right py-4 px-6 font-medium">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {blockierteZeiten.map((bz) => (
                    <tr key={bz.id} className="border-b border-border last:border-b-0 hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <span className="font-medium">{bz.titel}</span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-text-muted" />
                          <span>{formatDate(bz.startDatum)}</span>
                          {bz.startDatum !== bz.endDatum && (
                            <>
                              <span>-</span>
                              <span>{formatDate(bz.endDatum)}</span>
                            </>
                          )}
                        </div>
                        {!bz.ganztags && (
                          <div className="text-sm text-text-muted mt-1">
                            {bz.startzeit} - {bz.endzeit}
                          </div>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          bz.ganztags
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {bz.ganztags ? 'Ganztags' : 'Teilweise'}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-text-muted">
                        {bz.grund || '-'}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openEditModal(bz)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition"
                            title="Bearbeiten"
                          >
                            <Edit className="w-4 h-4 text-text-muted" />
                          </button>
                          <button
                            onClick={() => handleDelete(bz.id)}
                            className="p-2 hover:bg-red-50 rounded-lg transition"
                            title="Löschen"
                          >
                            <Trash2 className="w-4 h-4 text-danger" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title={editingItem ? 'Blockierung bearbeiten' : 'Neue Blockierung'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Titel */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Titel <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              name="titel"
              value={formData.titel}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="z.B. Weihnachtsurlaub, Betriebsferien..."
              required
            />
          </div>

          {/* Datum */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Von <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                name="startDatum"
                value={formData.startDatum}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">
                Bis <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                name="endDatum"
                value={formData.endDatum}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>
          </div>

          {/* Ganztags */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="ganztags"
                checked={formData.ganztags}
                onChange={handleChange}
                className="w-5 h-5 rounded border-border text-primary focus:ring-primary"
              />
              <span className="font-medium">Ganztägig blockiert</span>
            </label>
          </div>

          {/* Uhrzeiten (nur wenn nicht ganztags) */}
          {!formData.ganztags && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Von Uhrzeit</label>
                <select
                  name="startzeit"
                  value={formData.startzeit}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {timeOptions.map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Bis Uhrzeit</label>
                <select
                  name="endzeit"
                  value={formData.endzeit}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {timeOptions.map(time => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Grund */}
          <div>
            <label className="block text-sm font-medium mb-2">Grund (optional)</label>
            <textarea
              name="grund"
              value={formData.grund}
              onChange={handleChange}
              rows="2"
              className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="z.B. Familienurlaub, Renovierung..."
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-primary text-white py-3 rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50"
            >
              {saving ? 'Speichern...' : editingItem ? 'Aktualisieren' : 'Erstellen'}
            </button>
            <button
              type="button"
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
              className="flex-1 bg-gray-200 text-text py-3 rounded-button font-medium hover:bg-gray-300 transition"
            >
              Abbrechen
            </button>
          </div>
        </form>
      </Modal>
    </Layout>
  );
}

export default BlockierteZeiten;
